import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter 
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useToast } from "@/components/ui/use-toast"
import { 
  ArrowLeft, 
  Plus, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  ListChecks, 
  Repeat,
  AlertCircle,
  Search,
  Filter,
  X,
  RefreshCw,
  Calendar as CalendarIcon,
  MoreHorizontal,
  PlayCircle,
  PauseCircle,
  StopCircle,
  Loader2,
  ArrowUpRight,
  LayoutGrid,
  List
} from "lucide-react"
import { supabase } from "@/lib/supabase"
import { formatDate, cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { StatusBadge } from "@/components/common/StatusBadge"

interface PlaybookRun {
  id: string
  name: string
  description: string | null
  status: string
  started_at: string | null
  completed_at: string | null
  subscription_id: string | null
  customer_name?: string
  playbook_template_id?: string
  template_name?: string
  created_at: string
  updated_at: string
  task_count?: number
  completed_task_count?: number
}

export default function PlaybookRuns() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [runs, setRuns] = useState<PlaybookRun[]>([])
  const [filteredRuns, setFilteredRuns] = useState<PlaybookRun[]>([])
  const [isLoading, setIsLoading] = useState(true)
  
  // Search and filtering
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(9)
  const [totalPages, setTotalPages] = useState(1)
  
  // UI state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  
  useEffect(() => {
    loadPlaybookRuns()
  }, [])
  
  useEffect(() => {
    applyFilters()
  }, [runs, searchTerm, statusFilter])
  
    const loadPlaybookRuns = async () => {
      try {
        setIsLoading(true)
        
      // Fetch playbook runs with customer and template information
        const { data, error } = await supabase
          .from('playbook_runs')
        .select(`
          *,
          subscription:subscription_id (
            id,
            customer_relationship (
              id,
              child:customers!child_id (
                id,
                name,
                domain
              )
            )
          ),
          template:playbook_template_id (
            id,
            name
          )
        `)
          .order('created_at', { ascending: false })

        if (error) {
        console.error('Erro ao carregar execuções de playbook:', error)
          throw error
        }

      // Process data to include customer name and template name
      const processedData = data.map(run => ({
        ...run,
        customer_name: run.subscription?.customer_relationship?.child?.name || 'Cliente Desconhecido',
        template_name: run.template?.name || 'Template Desconhecido',
        // Mock task counts for now - would ideally come from backend
        task_count: Math.floor(Math.random() * 20) + 5,
        completed_task_count: Math.floor(Math.random() * 10)
      }))

      setRuns(processedData)
      setFilteredRuns(processedData)
      setTotalPages(Math.ceil(processedData.length / pageSize))
      } catch (error) {
      console.error('Erro:', error)
        toast({
        title: "Erro ao carregar execuções de playbook",
        description: "Não foi possível carregar as execuções de playbook.",
          variant: "destructive"
        })
      } finally {
        setIsLoading(false)
      }
    }

  const applyFilters = () => {
    let filtered = [...runs]
    
    // Apply text search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(run => 
        run.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        run.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        run.template_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(run => run.status.toLowerCase() === statusFilter.toLowerCase())
    }
    
    setFilteredRuns(filtered)
    setCurrentPage(1)
    setTotalPages(Math.ceil(filtered.length / pageSize))
  }

  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
  }

  // Function to get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredRuns.slice(startIndex, endIndex)
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />
      case 'pending':
        return <Clock className="h-5 w-5 text-blue-500" />
      case 'running':
        return <PlayCircle className="h-5 w-5 text-amber-500" />
      case 'cancelled':
        return <StopCircle className="h-5 w-5 text-red-500" />
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      default:
        return <Clock className="h-5 w-5 text-gray-500" />
    }
  }

  const getInitials = (name: string): string => {
    if (!name) return '?'
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  const getProgressPercentage = (run: PlaybookRun): number => {
    if (!run.task_count || run.task_count === 0) return 0
    return Math.round((run.completed_task_count || 0) / run.task_count * 100)
  }

  const getStatsCards = () => {
    const totalRuns = runs.length
    const completedRuns = runs.filter(run => run.status.toLowerCase() === 'completed').length
    const runningRuns = runs.filter(run => run.status.toLowerCase() === 'running').length
    const pendingRuns = runs.filter(run => run.status.toLowerCase() === 'pending').length
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Total de Playbooks</CardTitle>
            <ListChecks className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">{totalRuns}</div>
            <p className="text-xs text-muted-foreground">
              Execuções de playbook no sistema
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Concluídos</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">{completedRuns}</div>
            <p className="text-xs text-muted-foreground">
              {totalRuns > 0 ? `${Math.round((completedRuns / totalRuns) * 100)}% do total` : '0% do total'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Em execução</CardTitle>
            <PlayCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">{runningRuns}</div>
            <p className="text-xs text-muted-foreground">
              Execuções de playbook ativas
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">{pendingRuns}</div>
            <p className="text-xs text-muted-foreground">
              Aguardando para iniciar
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const getRunCard = (run: PlaybookRun) => {
    const progressPercentage = getProgressPercentage(run)
    
    return (
      <Card key={run.id} className="overflow-hidden hover:shadow-md transition-all">
        <CardHeader className="p-4 pb-2 flex flex-row items-start justify-between space-y-0">
          <div className="flex items-start gap-2">
            <div className="mt-1">{getStatusIcon(run.status)}</div>
            <div>
              <CardTitle className="text-lg">{run.name}</CardTitle>
              <CardDescription>
                {run.template_name}
              </CardDescription>
            </div>
          </div>
          <div>
            <StatusBadge status={run.status} context="job-execution" />
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-2 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                {getInitials(run.customer_name || '')}
              </AvatarFallback>
            </Avatar>
            <span>{run.customer_name}</span>
          </div>
          
          {run.status.toLowerCase() === 'running' && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>Progresso</span>
                <span>{progressPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-primary h-1.5 rounded-full" 
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>Iniciado: {run.started_at ? formatDate(run.started_at) : 'Não iniciado'}</span>
          </div>
          
          {run.completed_at && (
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              <span>Concluído: {formatDate(run.completed_at)}</span>
            </div>
          )}
        </CardContent>
        <CardFooter className="p-4 pt-0 flex justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            className="gap-1"
            onClick={() => navigate(`/customer-success/playbooks/runs/${run.id}`)}
          >
            <ArrowUpRight className="h-4 w-4" />
            Ver Detalhes
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/customer-success/playbooks/runs/${run.id}`)}>
                <ArrowUpRight className="h-4 w-4 mr-2" />
                Ver Detalhes
              </DropdownMenuItem>
              
              {run.status.toLowerCase() === 'pending' && (
                <DropdownMenuItem>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Iniciar Execução
                </DropdownMenuItem>
              )}
              
              {run.status.toLowerCase() === 'running' && (
                <DropdownMenuItem>
                  <PauseCircle className="h-4 w-4 mr-2" />
                  Pausar Execução
                </DropdownMenuItem>
              )}
              
              {['pending', 'running'].includes(run.status.toLowerCase()) && (
                <DropdownMenuItem>
                  <StopCircle className="h-4 w-4 mr-2" />
                  Cancelar Execução
                </DropdownMenuItem>
              )}
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem>
                <Repeat className="h-4 w-4 mr-2" />
                Duplicar Execução
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardFooter>
      </Card>
    )
  }

  const getRunList = () => {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Nome</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Iniciado</TableHead>
              <TableHead>Concluído</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getCurrentPageItems().length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  Nenhum resultado encontrado.
                </TableCell>
              </TableRow>
            ) : (
              getCurrentPageItems().map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="font-extrabold">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(run.status)}
                      <span>{run.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {getInitials(run.customer_name || '')}
                        </AvatarFallback>
                      </Avatar>
                      <span>{run.customer_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{run.template_name}</TableCell>
                  <TableCell><StatusBadge status={run.status} context="job-execution" /></TableCell>
                  <TableCell>{run.started_at ? formatDate(run.started_at) : 'Não iniciado'}</TableCell>
                  <TableCell>{run.completed_at ? formatDate(run.completed_at) : '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => navigate(`/customer-success/playbooks/runs/${run.id}`)}
                      >
                        <ArrowUpRight className="h-4 w-4" />
                        <span className="sr-only">Ver detalhes</span>
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Abrir menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/customer-success/playbooks/runs/${run.id}`)}>
                            <ArrowUpRight className="h-4 w-4 mr-2" />
                            Ver Detalhes
                          </DropdownMenuItem>
                          {run.status.toLowerCase() === 'pending' && (
                            <DropdownMenuItem>
                              <PlayCircle className="h-4 w-4 mr-2" />
                              Iniciar Execução
                            </DropdownMenuItem>
                          )}
                          {run.status.toLowerCase() === 'running' && (
                            <DropdownMenuItem>
                              <PauseCircle className="h-4 w-4 mr-2" />
                              Pausar Execução
                            </DropdownMenuItem>
                          )}
                          {['pending', 'running'].includes(run.status.toLowerCase()) && (
                            <DropdownMenuItem>
                              <StopCircle className="h-4 w-4 mr-2" />
                              Cancelar Execução
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <Repeat className="h-4 w-4 mr-2" />
                            Duplicar Execução
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-extrabold">Execuções de Playbook</h1>
              <p className="text-muted-foreground">
                Visualize e gerencie todas as execuções de playbook
              </p>
            </div>
          </div>
          
          {/* Loading skeleton for grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardHeader className="p-4">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </CardContent>
                <CardFooter className="p-4">
                  <Skeleton className="h-8 w-24" />
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="w-full px-6 py-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header and Actions */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">Execuções de Playbook</h1>
            <p className="text-muted-foreground">
              Visualize e gerencie todas as execuções de playbook
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/customer-success/playbooks")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <Button onClick={() => navigate("/customer-success/playbooks/runs/new")}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Execução
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {getStatsCards()}

        {/* Search and Filtering */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Buscar playbooks..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                    onClick={() => setSearchTerm('')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              <div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      <span>Status: {statusFilter === 'all' ? 'Todos' : 
                               statusFilter === 'completed' ? 'Concluído' : 
                               statusFilter === 'running' ? 'Em execução' : 
                               statusFilter === 'pending' ? 'Pendente' : 
                               statusFilter === 'cancelled' ? 'Cancelado' : 
                               statusFilter === 'failed' ? 'Falhou' : 
                               statusFilter}</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Status</SelectItem>
                    <SelectItem value="completed">Concluído</SelectItem>
                    <SelectItem value="running">Em execução</SelectItem>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                    <SelectItem value="failed">Falhou</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-center">
                <div className="bg-muted rounded-md p-1 flex">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => setViewMode('grid')}
                  >
                    <LayoutGrid className="h-4 w-4 mr-2" />
                    Grade
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4 mr-2" />
                    Lista
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={loadPlaybookRuns}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
                {(searchTerm || statusFilter !== 'all') && (
                  <Button variant="ghost" onClick={clearFilters} size="icon">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Playbook Runs Display */}
        <div className="mb-10">
          {filteredRuns.length === 0 ? (
            <Card className="p-10 text-center">
              <div className="flex flex-col items-center justify-center">
                <ListChecks className="h-16 w-16 text-muted-foreground/40 mb-4" />
                <h3 className="text-xl font-extrabold mb-2">Nenhuma execução de playbook encontrada</h3>
                <p className="text-muted-foreground mb-6">
                  Nenhuma execução de playbook corresponde aos seus critérios de busca.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={clearFilters}>
                    Limpar filtros
                  </Button>
                  <Button onClick={() => navigate("/customer-success/playbooks/runs/new")}>
                    Criar nova execução
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {getCurrentPageItems().map(run => getRunCard(run))}
                </div>
              ) : (
                getRunList()
              )}
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center space-x-2">
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue placeholder={pageSize.toString()} />
                    </SelectTrigger>
                    <SelectContent side="top">
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="9">9</SelectItem>
                      <SelectItem value="12">12</SelectItem>
                      <SelectItem value="24">24</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Mostrando <span className="font-extrabold">{Math.min((currentPage - 1) * pageSize + 1, filteredRuns.length)}</span> a{" "}
                    <span className="font-extrabold">{Math.min(currentPage * pageSize, filteredRuns.length)}</span> de{" "}
                    <span className="font-extrabold">{filteredRuns.length}</span> resultados
                  </p>
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                        className={cn(currentPage === 1 && "pointer-events-none opacity-50")}
                      />
                    </PaginationItem>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <PaginationItem key={i}>
                        <PaginationLink 
                          isActive={currentPage === i + 1}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                        className={cn(currentPage === totalPages && "pointer-events-none opacity-50")}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}